import { Employee } from '@/types/employees';

/**
 * Formats an employee address into a readable string.
 *
 * Combines address components (street, city, state, zip, and optionally country)
 * into a comma-separated string. Filters out empty or null values.
 *
 * @param address - The employee address object containing street, city, state, zip, and country
 * @param includeCountry - Whether to include the country in the formatted address. Defaults to false
 * @returns A formatted address string, or empty string if no address is provided
 *
 * @example
 * ```typescript
 * const address = {
 *   streetAddress1: '123 Main St',
 *   streetAddress2: 'Apt 4B',
 *   city: 'New York',
 *   state: 'NY',
 *   zip: '10001',
 *   country: 'USA'
 * };
 *
 * formatAddress(address); // "123 Main St Apt 4B, New York, NY, 10001"
 * formatAddress(address, true); // "123 Main St Apt 4B, New York, NY, 10001, USA"
 * formatAddress(null); // ""
 * ```
 */
export const formatAddress = (
  address: Employee['address'],
  includeCountry = false
) => {
  if (!address) return '';

  const streetAddress = [address.streetAddress1, address.streetAddress2]
    .filter(Boolean)
    .join(' ');

  const addressParts = [
    streetAddress,
    address.city,
    address.state,
    address.zip,
  ];

  if (includeCountry) {
    addressParts.push(address.country);
  }

  return addressParts.filter(Boolean).join(', ');
};
